import { createReducer } from '@reduxjs/toolkit'

import {
  AuthenticationState,
  TAzureAdAttributes
} from 'types/GlobalAuthentication'

import { DashboardStatus } from 'types/GlobalDashboardGroup'

import * as actions from '../../actions/Authentication/actions'
import * as adminActions from '../../actions/Admin/Authentication/actions'
import * as dashboardActions from '../../actions/Dashboards/actions'
import * as dashboardGroupActions from '../../actions/DashboardGroups/actions'
import * as dataSettingsActions from '../../actions/DataSettings/actions'
import * as selfActions from '../../actions/Self/actions'

// Initial state
const INITIAL_STATE: AuthenticationState = {
  isAuthenticated: false,
  customer: null,
  account: null,
  isDemo: false,
  validating: false,
  azureAd: {
    fetched: false,
    metadataUrl: null,
    domains: [],
    groupId: null,
    idpGroups: []
  },
  updatingUser: false,
  showChat: false
}

const toAzureAdState = (
  attributes: TAzureAdAttributes
): AuthenticationState['azureAd'] => ({
  fetched: true,
  metadataUrl: attributes.metadata_url,
  domains: attributes.domains,
  groupId: attributes.group_id,
  idpGroups: attributes.idp_groups || []
})

// Reducer
const AuthenticationReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(actions.validateUser, (state) => {
      state.validating = true
    })
    .addCase(actions.setSignedIn, (state, action) => {
      const { payload } = action

      state.customer = payload.customer
      state.account = payload.account
      state.isAuthenticated = true
      state.isDemo = payload.is_demo
      state.validating = false
      state.showChat = ![
        '1d18c8fd-887c-44e2-a860-14e5893fca6c',
        '050864ec-1f92-4c0a-bb2f-c4a5d997d08f',
        'ef268f8a-b708-4047-9bfc-d3f638ad71c5',
        '86a1d9e4-98ba-4f03-ada6-7d355c5f0bac'
      ].includes(payload.customer.customer_id)
    })
    .addCase(selfActions.updateSelf, (state) => {
      state.updatingUser = true
    })
    .addCase(selfActions.updateSelfSuccess, (state, action: any) => {
      const { payload } = action

      state.account = { ...state.account, ...payload.attributes }
      state.updatingUser = false
    })
    .addCase(selfActions.updateSelfFailed, (state) => {
      state.updatingUser = false
    })
    .addCase(selfActions.updateEconomySuccess, (state, action) => {
      const { payload } = action

      if (state.customer) {
        state.customer.period_type = payload.period_type
        state.customer.latest_closed_period = payload.latest_closed_period
      }
    })
    .addCase(adminActions.getAzureAdSuccess, (state, action) => {
      state.azureAd = toAzureAdState(action.payload.attributes)
    })
    .addCase(adminActions.putAzureAdSuccess, (state, action) => {
      state.azureAd = toAzureAdState(action.payload.attributes)
    })
    .addCase(adminActions.deleteAzureAdSuccess, (state) => {
      state.azureAd = INITIAL_STATE.azureAd
    })
    .addCase(dashboardGroupActions.destroySuccess, (state, action) => {
      if (
        state.customer &&
        action.payload.status === DashboardStatus.PUBLISHED
      ) {
        state.customer.number_of_dashboards_in_use =
          state.customer.number_of_dashboards_in_use - 1
      }
    })
    .addCase(dashboardActions.createSuccess, (state, action) => {
      const { payload } = action

      if (
        state.customer &&
        payload.dashboard.index === 0 &&
        payload.group.status === DashboardStatus.PUBLISHED
      ) {
        state.customer.number_of_dashboards_in_use =
          state.customer.number_of_dashboards_in_use + 1
      }
    })
    // Publishing an existing draft group / unpublishing a published one changes
    // the in-use count the same way create/destroy of a published group does.
    .addCase(dashboardGroupActions.publishStatusChanged, (state, action) => {
      if (!state.customer) {
        return
      }

      if (action.payload.status === DashboardStatus.PUBLISHED) {
        state.customer.number_of_dashboards_in_use =
          state.customer.number_of_dashboards_in_use + 1
      } else if (action.payload.status === DashboardStatus.DRAFT) {
        state.customer.number_of_dashboards_in_use =
          state.customer.number_of_dashboards_in_use - 1
      }
    })
    .addCase(dataSettingsActions.getLatestJobRunSuccess, (state, action) => {
      if (state.customer) {
        state.customer.latest_job_run = action.payload.latest_job_run
      }
    })
    .addCase(actions.signOut, () => INITIAL_STATE)
    .addDefaultCase((state) => state)
})

export default AuthenticationReducer
